import { ref, computed, watch, type Ref } from 'vue';

import { onClickOutside, useElementHover } from '@vueuse/core';

interface UseTooltipStateOptions {
  /** External control over the tooltip's visibility. Should override internal state */
  isActive: Ref<boolean | undefined>;
  trigger?: Ref<'click' | 'hover'>;
}

const HOVER_LOSS_DELAY = 100; // milliseconds

export function useTooltipState(root: Ref<HTMLElement | null>, options: UseTooltipStateOptions) {
  const { isActive: isActiveExternal, trigger } = options;

  const isActiveLocal = ref(false);

  const isActiveState = computed(() => {
    return typeof isActiveExternal.value === 'boolean' ? isActiveExternal.value : isActiveLocal.value;
  });

  const isHovered = useElementHover(root);

  let hoverTimeoutID: ReturnType<typeof setTimeout> | null = null;

  watch(isHovered, (hovered) => {
    if (trigger?.value !== 'hover') return;

    if (!hovered) {
      hoverTimeoutID = setTimeout(() => {
        isActiveLocal.value = false;
      }, HOVER_LOSS_DELAY);
    } else {
      clearTimeout(hoverTimeoutID);
      hoverTimeoutID = null;
      isActiveLocal.value = true;
    }
  });

  function onClick() {
    if (trigger?.value !== 'click') return;

    isActiveLocal.value = !isActiveLocal.value;
  }

  onClickOutside(root, () => {
    if (trigger?.value !== 'click') return;

    if (isActiveLocal.value) {
      isActiveLocal.value = false;
    }
  });

  return {
    isActiveState,
    onClick,
  }
}